import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, createBlock as _createBlock, withModifiers as _withModifiers, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mb-10"
}
const _hoisted_2 = { class: "h-full w-full flex items-center justify-center flex-col gap-2" }
const _hoisted_3 = /*#__PURE__*/_createTextVNode("Your inspection images have been archived.")
const _hoisted_4 = /*#__PURE__*/_createTextVNode("To view your images, assemblies, and reports again, please click the button below to re-import the data.")
const _hoisted_5 = /*#__PURE__*/_createTextVNode("Note: The re-import process may take some time depending on the data volume.")
const _hoisted_6 = /*#__PURE__*/_createTextVNode("Re-import Data")
const _hoisted_7 = /*#__PURE__*/_createTextVNode("ALL IMAGES FOR THIS TURBINE WILL BE RE-IMPORTED")
const _hoisted_8 = /*#__PURE__*/_createTextVNode("THIS MAY TAKE TIME")
const _hoisted_9 = /*#__PURE__*/_createTextVNode("START")
const _hoisted_10 = /*#__PURE__*/_createTextVNode("CANCEL")
const _hoisted_11 = {
  key: 0,
  class: "uppercase"
}
const _hoisted_12 = {
  key: 1,
  class: "uppercase"
}
const _hoisted_13 = {
  key: 2,
  class: "uppercase"
}
const _hoisted_14 = { class: "uppercase" }
const _hoisted_15 = { class: "uppercase" }
const _hoisted_16 = { class: "uppercase" }
const _hoisted_17 = { key: 0 }
const _hoisted_18 = { key: 1 }
const _hoisted_19 = { key: 0 }
const _hoisted_20 = { key: 1 }
const _hoisted_21 = { key: 1 }
const _hoisted_22 = ["title"]
const _hoisted_23 = ["src", "alt"]

import { uniq } from 'lodash';

import { useRoute } from 'vue-router';
import { computed, ref, Ref, PropType, inject } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  NkTable,
  NkTableColumn,
  NkBadge,
  NkModal,
  NkGallery,
  NkStack,
  NkText,
  NkInlineModal,
  NkButton,
  NkGroup,
} from '@/core/components';

import NkVoltaMeasureUpdateForm
  from '@/entities/volta-measure/components/form/VoltaMeasureUpdateForm.vue';

import { ITEM_PER_PAGE, api, getDomainPath } from '@/services';
import { resume } from '@/plugins/resume';

import { useGetVoltaMeasuresQuery } from '@/entities/volta-measure/hooks';
import { useGetInspectionQuery } from '@/entities/inspection/hooks';
import { useGetDeviceQuery } from '@/entities/device/hooks';
import { useGetAnnotationSeveritiesQuery } from '@/entities/annotation-severity/hooks';
import { useGetVoltaItemsQuery } from '@/entities/volta-item/hooks';

import { VoltaMeasure } from '@/entities/volta-measure/types';
import { ServiceInject, serviceInjectionKey } from '@/entities/services/keys';
import { ElNotification } from 'element-plus';


export default /*#__PURE__*/_defineComponent({
  __name: 'VoltaMeasureTable',
  props: {
  measuresIds: {
    type: Array as PropType<VoltaMeasure['id'][]>,
    default: () => [],
    required: true,
  },
},
  setup(__props) {

const props = __props

const route = useRoute();
const { locale } = useI18n();

const wrapper: Ref<HTMLElement|undefined> = ref();
const getWrapperHeight = computed(() => (wrapper.value as HTMLElement).clientHeight);
const enableReImportMessage = ref(false)

const toUpdateVoltaMeasure: Ref<VoltaMeasure['id'] | null> = ref(null);
const hasToUpdateVoltaMeasure = computed(() => !!toUpdateVoltaMeasure.value);



const page = ref(1);
const canFetchMore = ref(false);
const { isCertifiedVolta, isVoltaExpress, isVoltaPlus } = inject(serviceInjectionKey) as ServiceInject;

// Fetching for VoltaMeasures
const voltaMeasuresIds = computed(() => props.measuresIds || []);
const hasVoltaMeasuresIds = computed(() => !!voltaMeasuresIds.value.length);
const voltaMeasuresQueryParams = computed(() => ({
  ids: [...voltaMeasuresIds.value],
  page: page.value,
}));
const { data: voltaMeasures, isLoading } = useGetVoltaMeasuresQuery({
  enabled: hasVoltaMeasuresIds,
  onSuccess: (data) => {
    canFetchMore.value = data.length >= ITEM_PER_PAGE;
  },
}, voltaMeasuresQueryParams);

const voltaItemQueryFilters: Ref = computed(() => ({ ids: uniq(voltaMeasures.value?.map(o => o.voltaItem)) }));
const hasVoltaItemIds = computed(() => !!voltaItemQueryFilters.value.ids.length);
const { data: voltaItems } = useGetVoltaItemsQuery({ enabled: hasVoltaItemIds }, voltaItemQueryFilters);

// Fetching for severities
const severitiesQueryFilters: Ref = computed(() => ({ ids: uniq(voltaMeasures.value?.map(o => o.severity)).filter(Boolean) }));
const hasSeveritiesIds = computed(() => !!severitiesQueryFilters.value.ids.length);
const { data: severities } = useGetAnnotationSeveritiesQuery({ enabled: hasSeveritiesIds }, severitiesQueryFilters);

// Mapping
const getVoltaMeasures = computed(() => voltaMeasures.value?.filter(v => props.measuresIds?.includes(v.id))?.map(v => ({
  ...v,
  severity: severities.value?.find(s => s.id === v.severity),
  voltaItem: voltaItems.value?.find(item => item.id === v.voltaItem) ?? null,
})) || []);

const inspectionQueryData: Ref = computed(() => ({
  id: route.params.inspection,
}));
const hasInspectionQueryData = computed(() => !!inspectionQueryData.value.id);
const { data: inspection } = useGetInspectionQuery(inspectionQueryData, {
  enabled: hasInspectionQueryData,
});

// Fetching for Device
const deviceQueryData: Ref = computed(() => ({
  id: inspection.value?.device,
}));
const hasDeviceQueryData = computed(() => !!deviceQueryData.value.id);
const { data: device } = useGetDeviceQuery(deviceQueryData, {
  enabled: hasDeviceQueryData,
});
const getDeviceName = computed(() => device.value?.deviceValues[0].value);

const handleRowClick = ({ id }: VoltaMeasure) => {
  toUpdateVoltaMeasure.value = id;
};

const enableReImportButton = () => {
  enableReImportMessage.value = true
}

const ReImportModal = ref()
const closeModal = () => {
  ReImportModal.value?.closeDialog()
}

const sendAndCloseModal = async () => {
  await api.get(`/inspections/${inspection.value?.id ?? ''}/re-import`)
  ElNotification({
    type: 'success',
    position: 'bottom-right',
    title: 'Request submited',
    message: 'Your request just got submited to Supairvision team'
  })
  ReImportModal.value?.closeDialog()
}

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (enableReImportMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_unref(NkStack), { align: "center" }, {
              default: _withCtx(() => [
                _createVNode(_unref(NkText), { class: "!italic !text-lg !text-orange-500" }, {
                  default: _withCtx(() => [
                    _hoisted_3
                  ]),
                  _: 1
                }),
                _createVNode(_unref(NkText), { class: "!italic !text-base" }, {
                  default: _withCtx(() => [
                    _hoisted_4
                  ]),
                  _: 1
                }),
                _createVNode(_unref(NkText), { class: "!italic !text-xs" }, {
                  default: _withCtx(() => [
                    _hoisted_5
                  ]),
                  _: 1
                }),
                _createVNode(_unref(NkInlineModal), {
                  ref_key: "ReImportModal",
                  ref: ReImportModal
                }, {
                  content: _withCtx(() => [
                    _createVNode(_unref(NkStack), { align: "center" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(NkText), { class: "!italic !font-bold !text-lg" }, {
                          default: _withCtx(() => [
                            _hoisted_7
                          ]),
                          _: 1
                        }),
                        _createVNode(_unref(NkText), { class: "!italic !font-bold !text-lg !text-orange-500" }, {
                          default: _withCtx(() => [
                            _hoisted_8
                          ]),
                          _: 1
                        }),
                        _createVNode(_unref(NkGroup), { align: "stretch" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(NkButton), {
                              type: "success",
                              onClick: sendAndCloseModal
                            }, {
                              default: _withCtx(() => [
                                _hoisted_9
                              ]),
                              _: 1
                            }),
                            _createVNode(_unref(NkButton), {
                              type: "danger",
                              onClick: closeModal
                            }, {
                              default: _withCtx(() => [
                                _hoisted_10
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_unref(NkButton), { icon: "NkDownload" }, {
                      default: _withCtx(() => [
                        _hoisted_6
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 512)
              ]),
              _: 1
            })
          ])
        ]))
      : _createCommentVNode("", true),
    _withDirectives((_openBlock(), _createElementBlock("div", {
      class: "flex-1 overflow-auto min-h-0",
      ref_key: "wrapper",
      ref: wrapper
    }, [
      (wrapper.value)
        ? (_openBlock(), _createBlock(_unref(NkTable), {
            key: 0,
            data: _unref(getVoltaMeasures),
            height: _unref(getWrapperHeight),
            clickable: "",
            onRowClick: handleRowClick
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(NkTableColumn), {
                label: _ctx.$t('misc.volta_component')
              }, {
                default: _withCtx((scope) => [
                  (scope.row.parentDeviceName.toLowerCase() === 'tower')
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(scope.row.parentDeviceName), 1))
                    : (scope.row.deviceName !== 'SS' && scope.row.deviceName !== 'PS' && scope.row.deviceName !== 'Tip' && scope.row.voltaItem !== null)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(scope.row.deviceName) + " - " + _toDisplayString(scope.row.voltaItem.name[_unref(locale)]), 1))
                      : (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(scope.row.parentDeviceName) + " - " + _toDisplayString(scope.row.deviceName), 1))
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(_unref(NkTableColumn), {
                label: 
          `${_ctx.$t('misc.hub_distance')} ${([3, 4, 5, 6].includes(Number(_unref(device)?.type)) ? _ctx.$t('misc.from_hub') : _ctx.$t('misc.from_ground'))}`
        ,
                width: "150"
              }, {
                default: _withCtx((scope) => [
                  _createElementVNode("span", _hoisted_14, _toDisplayString(scope.row.positionFromHub ?? "NA"), 1)
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(_unref(NkTableColumn), {
                label: _ctx.$t('volta-measure.table.resistance')
              }, {
                default: _withCtx((scope) => [
                  _createElementVNode("span", _hoisted_15, _toDisplayString(scope.row.measureValue ?? "NA"), 1)
                ]),
                _: 1
              }, 8, ["label"]),
              (_unref(isVoltaPlus))
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createVNode(_unref(NkTableColumn), {
                      label: _ctx.$t('volta-measure.table.amperage')
                    }, {
                      default: _withCtx((scope) => [
                        _createElementVNode("span", null, _toDisplayString(scope.row.amperage ?? "NA"), 1)
                      ]),
                      _: 1
                    }, 8, ["label"]),
                    _createVNode(_unref(NkTableColumn), {
                      label: _ctx.$t('volta-measure.table.voltage')
                    }, {
                      default: _withCtx((scope) => [
                        _createElementVNode("span", _hoisted_16, _toDisplayString(scope.row.voltage ?? "NA"), 1)
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ], 64))
                : _createCommentVNode("", true),
              (_unref(isCertifiedVolta))
                ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
                    key: 1,
                    label: _ctx.$t('misc.status')
                  }, {
                    default: _withCtx((scope) => [
                      (scope.row.severity)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(scope.row.isInspected ? _ctx.$t('misc.inspected') : _ctx.$t('misc.non-inspected')), 1))
                        : (_openBlock(), _createElementBlock("span", _hoisted_18, "NA"))
                    ]),
                    _: 1
                  }, 8, ["label"]))
                : _createCommentVNode("", true),
              (_unref(isCertifiedVolta))
                ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
                    key: 2,
                    label: _ctx.$t('misc.conformity')
                  }, {
                    default: _withCtx((scope) => [
                      (scope.row.severity)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_19, _toDisplayString(scope.row.isCompliant ? _ctx.$t('misc.compliant') : _ctx.$t('misc.non-compliant')), 1))
                        : (_openBlock(), _createElementBlock("span", _hoisted_20, "NA"))
                    ]),
                    _: 1
                  }, 8, ["label"]))
                : _createCommentVNode("", true),
              _createVNode(_unref(NkTableColumn), {
                label: _ctx.$t('misc.severity')
              }, {
                default: _withCtx((scope) => [
                  (scope.row.severity)
                    ? (_openBlock(), _createBlock(_unref(NkBadge), {
                        key: 0,
                        plain: "",
                        size: "sm",
                        color: scope.row.severity?.color
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(scope.row.severity?.name || '-'), 1)
                        ]),
                        _: 2
                      }, 1032, ["color"]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_21, "NA"))
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(_unref(NkTableColumn), {
                label: _ctx.$t('misc.comment')
              }, {
                default: _withCtx((scope) => [
                  _createElementVNode("span", {
                    title: scope.row.expertComment[_unref(locale)]
                  }, _toDisplayString(_unref(resume)(scope.row.expertComment[_unref(locale)], 50) || '-'), 9, _hoisted_22)
                ]),
                _: 1
              }, 8, ["label"]),
              (!_unref(isVoltaExpress))
                ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
                    key: 3,
                    label: _ctx.$t('misc.photo')
                  }, {
                    default: _withCtx((scope) => [
                      (scope.row.uri)
                        ? (_openBlock(), _createBlock(_unref(NkGallery), {
                            key: 0,
                            hasMiniatur: true
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("img", {
                                onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent","stop"])),
                                src: _unref(getDomainPath)(scope.row.uri.split('/uploads/')[1]),
                                alt: `${ _ctx.$t('misc.photo') }`,
                                onError: enableReImportButton
                              }, null, 40, _hoisted_23)
                            ]),
                            _: 2
                          }, 1024))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }, 8, ["label"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["data", "height"]))
        : _createCommentVNode("", true),
      (_ctx.$store.getters['connection/isGranted'](['volta_measure_edit']))
        ? (_openBlock(), _createBlock(_unref(NkModal), {
            key: 1,
            class: "max-w-screen-md",
            title: _ctx.$t('volta-measure.action.edit'),
            "model-value": _unref(hasToUpdateVoltaMeasure),
            onClose: _cache[2] || (_cache[2] = ($event: any) => (toUpdateVoltaMeasure.value = null))
          }, {
            default: _withCtx(() => [
              (toUpdateVoltaMeasure.value)
                ? (_openBlock(), _createBlock(NkVoltaMeasureUpdateForm, {
                    key: 0,
                    onSubmitted: _cache[1] || (_cache[1] = ($event: any) => (toUpdateVoltaMeasure.value = null)),
                    "volta-measure": toUpdateVoltaMeasure.value
                  }, null, 8, ["volta-measure"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["title", "model-value"]))
        : _createCommentVNode("", true)
    ])), [
      [_directive_loading, _unref(isLoading)]
    ])
  ], 64))
}
}

})